import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { cardCls, errMsg } from "../../components/admin/promotionsUi";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService } from "../../features/gamification/service";
import type { GamificationOverview, OverviewQuest } from "../../features/gamification/types";

import { InfoTh, LoadError, tdCls } from "./gamificationShared";

// ── Home: budget meter ───────────────────────────────────────────────────────
function Meter({ pct, label }: { pct: number | null; label: string }) {
  const p = pct === null ? 0 : Math.min(100, Math.max(0, pct));
  const tone = p >= 100 ? "bg-red-500" : p >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="h-2 w-full rounded-full bg-gray-800" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p)}>
      <div className={`h-2 rounded-full ${tone}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-gray-800/50 border border-gray-800 rounded-xl p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold text-white mt-1">{value}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

const nf = (n: number) => n.toLocaleString("en-IN");
const GROUP_LABEL: Record<string, string> = { checklist: "Checklist", first_time: "First time", referral: "Referral" };

function whoCanEarn(q: OverviewQuest): string {
  const roles = (q.audience_roles ?? []).map((r) => r.charAt(0).toUpperCase() + r.slice(1));
  return roles.length ? roles.join(" and ") : "—";
}

function creditsEach(q: OverviewQuest): string {
  if (q.reward_text) return q.reward_text;
  const roles = q.audience_roles ?? [];
  const per = roles.map((r) => q.reward_by_role?.[r] ?? q.reward_credits ?? 0);
  if (!roles.length) return q.reward_credits ? nf(q.reward_credits) : "—";
  if (new Set(per).size <= 1) return nf(per[0]);
  return roles.map((r, i) => `${r.charAt(0).toUpperCase() + r.slice(1)} ${nf(per[i])}`).join(" / ");
}

function QuestTable({ quests }: { quests: OverviewQuest[] }) {
  const [byMonth, setByMonth] = useState(true);
  const [hideZero, setHideZero] = useState(false);
  const rows = quests
    .filter((q) => !hideZero || q.grants > 0 || q.month_grants > 0)
    .map((q, i) => ({ q, i }))
    .sort((a, b) => (byMonth ? b.q.month_credits - a.q.month_credits || a.i - b.i : a.i - b.i))
    .map((r) => r.q);
  const sum = (k: "month_grants" | "month_credits" | "grants" | "credits") => rows.reduce((t, q) => t + q[k], 0);
  const numTd = `${tdCls} text-center tabular-nums`;
  return (
    <div>
      <p className="text-gray-400 text-xs mb-2">
        Each row is one quest. People paid = how many users received it. Credits paid = the total credits given for it.
      </p>
      <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-400">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={byMonth} onChange={(e) => setByMonth(e.target.checked)} />
          Sort by credits paid this month (untick for quest order)
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={!hideZero} onChange={(e) => setHideZero(!e.target.checked)} />
          Show quests nobody has earned yet
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs text-gray-500 align-bottom">
              <InfoTh label="Quest" tip="The quest name. Checklist = steps on the setup checklist. First time = a reward for doing something for the first time." />
              <InfoTh center label="Who can earn" tip="Which kind of user can earn this quest: Seller, Buyer or both." />
              <InfoTh center label="Credits each" tip="Credits one person gets for finishing the quest. Shown per role when sellers and buyers get different amounts." />
              <InfoTh center label="People paid this month" tip="How many users received this reward since the 1st of this month (India time)." />
              <InfoTh center label="Credits paid this month" tip="Total credits given for this quest since the 1st of this month." />
              <InfoTh center label="People paid all time" tip="How many users have ever received this reward." />
              <InfoTh center label="Credits paid all time" tip="Total credits ever given for this quest." />
              <InfoTh last center label="Budget" tip="The most credits this quest may pay in total. No limit = the quest has no cap of its own. Red = more than 80% used." />
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => {
              const pct = q.budget_pct ?? 0;
              return (
                <tr key={q.quest_id} className="border-t border-gray-800 text-gray-300 align-top">
                  <td className={tdCls}>
                    {q.name}{!q.enabled && <span className="text-gray-500 text-xs"> ({q.group === "referral" ? "referral is off" : "off"})</span>}
                    {q.group && <span className="block text-[11px] text-gray-500">{GROUP_LABEL[q.group] ?? q.group}</span>}
                  </td>
                  <td className={`${tdCls} text-center`}>{whoCanEarn(q)}</td>
                  <td className={`${tdCls} text-center`}>{creditsEach(q)}</td>
                  <td className={numTd}>{nf(q.month_grants)}</td>
                  <td className={numTd}>{nf(q.month_credits)}</td>
                  <td className={numTd}>{nf(q.grants)}</td>
                  <td className={numTd}>{nf(q.credits)}</td>
                  <td className="py-2 min-w-[11rem] text-center">
                    {q.budget_credits === null ? (
                      <span className="text-gray-500 text-xs">No limit</span>
                    ) : (
                      <>
                        <span className={`text-xs ${pct >= 100 ? "text-red-400" : pct > 80 ? "text-amber-400" : ""}`}>
                          {nf(q.budget_used)} of {nf(q.budget_credits)} credits used
                        </span>
                        <Meter pct={q.budget_pct} label={`${q.name} budget used`} />
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="py-3 text-gray-500 text-xs">Every quest is hidden. Tick "Show quests nobody has earned yet" to see them.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-700 text-gray-200 font-medium">
              <td className={tdCls} colSpan={3}>Total</td>
              <td className={numTd}>{nf(sum("month_grants"))}</td>
              <td className={numTd}>{nf(sum("month_credits"))}</td>
              <td className={numTd}>{nf(sum("grants"))}</td>
              <td className={numTd}>{nf(sum("credits"))}</td>
              <td className="py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export function OverviewPanel({ reloadKey }: { reloadKey: number }) {
  const [data, setData] = useState<GamificationOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await gamificationService.getOverview());
      setError("");
    } catch (e) {
      setData(null);
      setError(errMsg(e, "Failed to load the budget overview"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { setLoading(true); void load(); }, [load, reloadKey]);

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Budget and payouts</h2>
      <p className="text-gray-500 text-xs mb-4">
        How much of this month's budget has been used and what each quest has paid.
        <span className="block text-gray-600">Example: if the meter is red, new quest rewards stop until the budget is raised or the month turns.</span>
      </p>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data ? (
        <LoadError message={error} onRetry={() => { setLoading(true); void load(); }} />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Rewards" value={data.enabled ? "ON" : "OFF"} />
            <Stat label="Credits paid today (IST)" value={String(data.granted_today)} />
            <Stat label={`Credits paid in ${data.period}`} value={String(data.granted_this_month)} />
            <Stat
              label="Users at the 30-day limit"
              value={data.users_at_cap === null ? "No limit set" : String(data.users_at_cap)}
              hint={data.user_cap_30d === null ? undefined : `Users with ${data.user_cap_30d}+ credits in the last 30 days.`}
            />
          </div>
          {data.blocked && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Blocked by the 30-day limit (today / 7 days)" value={`${data.blocked.today.cap_30d} / ${data.blocked.last_7_days.cap_30d}`}
                hint="Each time a reward was held back because the user reached their 30-day limit." />
              <Stat label="Blocked by the monthly budget (today / 7 days)" value={`${data.blocked.today.monthly_budget} / ${data.blocked.last_7_days.monthly_budget}`} />
              <Stat label="Blocked by a quest budget (today / 7 days)" value={`${data.blocked.today.quest_budget} / ${data.blocked.last_7_days.quest_budget}`} />
              {data.scan && (
                <Stat label="Safety scan" value={data.scan.last_run_at ? formatISTDateTime(data.scan.last_run_at) : "Not run yet"}
                  hint={data.scan.last_result ? `Last run: ${data.scan.last_result.scanned} users checked, ${data.scan.last_result.recovered} missed rewards recovered, ${data.scan.last_result.errors} errors. Total recovered: ${data.scan.total_recovered}.` : undefined} />
              )}
            </div>
          )}
          <div>
            <div className="flex justify-between text-sm text-gray-300 mb-1">
              <span>Monthly budget</span>
              <span>
                {data.monthly_budget === null
                  ? `This month: ${nf(data.month_used)} credits used — no budget set`
                  : `This month: ${nf(data.month_used)} of ${nf(data.monthly_budget)} credits used (${data.month_pct ?? 0}%) — ${nf(data.month_remaining ?? 0)} left`}
              </span>
            </div>
            <Meter pct={data.month_pct} label="Monthly budget used" />
          </div>
          {data.quests.length === 0 ? (
            <p className="text-gray-500 text-sm">No quests are set up yet.</p>
          ) : (
            <QuestTable quests={data.quests} />
          )}
        </div>
      )}
    </section>
  );
}
